import { useContext, useState, useEffect } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { NewSerpContext } from '@/context/Context';
import { object, string } from 'yup';
import { Formik, type FormikHelpers } from 'formik';
import historyApi, {
  useStartSerpGettingMutation
} from '@/app/Serp/api/historyApi';
import { errorMessage, warningMessage } from '@/utils/toast';
import { setSerp } from '@/app/Serp/reducers/serpDataSlice';
import { useDispatch } from 'react-redux';
import Select, { type SingleValue } from 'react-select';
import TextLoading from '../../../common/components/skeleton/TextLoading';
import OptionLayoutPlace from '@/app/common/components/OptionsLayout/OptionLayoutPlace';
import SingleValueFlag from '@/app/common/components/OptionsLayout/SingleValueFlag';
import accessLevelApi, {
  useCheckAccessQuery
} from '@/app/common/api/accessLevelApi';
import {
  breakpointsMediaQuery,
  getColor,
  useMediaQuery
} from '@/helpers/utils';
import geoLanguagesData from '@/app/common/api/backups/languages.json';
import geoLocationsData from '@/app/common/api/backups/locations.json';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import useDomain from '@/hooks/useDomain';
import classNames from 'classnames';
import { useGetTariffInfoQuery } from '@/app/KeywordTracker/api/keywordsApi';

const isLoadingGeoLanguages = false;
const isLoadingGeoLocations = false;

type SelectOption = {
  value: string;
  label: string;
};

type CountryOption = SelectOption & {
  flag?: string;
};

type LatestKeyword = {
  keyword?: string;
  location_code?: string;
  language_code?: string;
};

type NewSerpFormProps = {
  latestKeyword?: LatestKeyword;
};

type NewSerpFormValues = {
  keyword?: string;
};

const NewSerpForm = ({ latestKeyword }: NewSerpFormProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLgUp = useMediaQuery(breakpointsMediaQuery.lg);
  const { keyword, loc, lang } = useParams<{
    keyword?: string;
    loc?: string;
    lang?: string;
  }>();
  const { domain_slug, domain } = useDomain();
  const [geoLocationsCountries, setGeoLocationsCountries] =
    useState<CountryOption[]>();
  const [geoLanguages, setGeoLanguages] = useState<SelectOption[]>();
  const [startSerp] = useStartSerpGettingMutation();

  const { data: userInfo } = useGetUserInfoQuery();

  const [selectedPlace, setSelectedPlace] = useState<CountryOption>({
    value: 'ir',
    label: 'ایران',
    flag: `https://static.mizfa.tools/flags/ir.webp`
  });

  const [selectedLanguage, setSelectedLanguage] = useState<SelectOption>({
    value: 'fa',
    label: 'فارسی'
  });

  const { isSubmitted, setIsSubmitted, setSerpId, setStep } =
    useContext(NewSerpContext);

  useEffect(() => {
    if (geoLocationsData) {
      const geoCountries: CountryOption[] = [];
      geoLocationsData.data.map(({ code, fa_name }) =>
        geoCountries.push({
          value: code,
          label: fa_name,
          flag: `https://static.mizfa.tools/flags/${code}.webp`
        })
      );

      setGeoLocationsCountries(geoCountries);

      if (loc && loc !== 'ir' && latestKeyword) {
        let geo: { code?: string; fa_name?: string } = {};
        if (loc === 'last')
          geo =
            geoLocationsData.data.find(
              item => item.code === latestKeyword?.location_code
            ) ?? {};
        else
          geo =
            geoLocationsData.data.find(
              item => item.code === loc.toLowerCase()
            ) ?? {};
        setSelectedPlace({
          value: geo.code || '',
          label: geo.fa_name || '',
          flag: `https://static.mizfa.tools/flags/${geo?.code}.webp`
        });
      }
    }
  }, [geoLocationsData]);

  useEffect(() => {
    if (geoLanguagesData) {
      const languages: SelectOption[] = [];
      geoLanguagesData.data.map(({ code, fa_name }) =>
        languages.push({
          value: code,
          label: fa_name
        })
      );

      setGeoLanguages(languages);

      if (lang && lang !== 'fa' && latestKeyword) {
        let geo: { code?: string; fa_name?: string } = {};
        if (lang === 'last')
          geo =
            geoLanguagesData.data.find(
              item => item.code === latestKeyword?.language_code
            ) ?? {};
        else
          geo =
            geoLanguagesData.data.find(item => item.code === lang) ?? {};

        setSelectedLanguage({
          value: geo.code || '',
          label: geo.fa_name || ''
        });
      }
    }
  }, [geoLanguagesData]);

  const { data: accessData } = useCheckAccessQuery({
    domain_slug,
    tool_name: 'serp'
  });

  const onSubmit = async (
    values: NewSerpFormValues,
    _actions: FormikHelpers<NewSerpFormValues>
  ) => {
    setIsSubmitted(true);
    setStep(2);
    await startSerp({
      requested_user: {
        name_family: `${userInfo?.data?.first_name} ${userInfo?.data?.last_name}`,
        email: userInfo?.data?.email
      },
      keyword: (values.keyword ?? '').trim(),
      location_code: selectedPlace.value || 'ir',
      language_code: selectedLanguage.value || 'fa',
      google_domain: 'google.com',
      domain_slug,
      use_agent: null
    })
      .unwrap()
      .then(response => {
        if (response.code !== 200) {
          warningMessage(String(response.data));
          setIsSubmitted(false);
          setStep(1);
        } else {
          setSerpId(response.data.serp_id ?? '');
          dispatch(setSerp(response.data));
          navigate(
            `/serp/overview/${domain_slug}/${domain}/${response.data.serp_id}/${selectedPlace.value}/${selectedLanguage.value}/${values.keyword}`
          );
          dispatch(historyApi.util.invalidateTags(['SerpStatus']));
          dispatch(accessLevelApi.util.invalidateTags(['AccessLevel']));
          setStep(3);
        }
      })
      .catch(err => {
        errorMessage(
          'در ارسال درخواست خطایی رخ داده هست لطفا مجددا تلاش کنید.'
        );
        setIsSubmitted(false);
        setStep(1);
        console.log(err);
      });
  };
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const isAccessAllowed = () =>
    trackerInfo?.data?.access_level !== 3 && !isFetchingTrackerInfo;

  return (
    <>
      {!isLoadingGeoLanguages && !isLoadingGeoLocations ? (
        <Formik<NewSerpFormValues>
          onSubmit={onSubmit}
          initialValues={{
            keyword: keyword !== 'last' ? keyword : latestKeyword?.keyword
          }}
          // اعتبار سنجی کلمه وارد شده برای خزش
          validationSchema={object().shape({
            keyword: string().required('وارد کردن کلمه الزامی می‌باشد')
          })}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit
          }) => (
            <div className={`w-100 w-lg-75 mt-3 mb-3`}>
              <Form onSubmit={handleSubmit}>
                <div
                  className={classNames('position-relative input-group-md', {
                    'input-group': isLgUp,
                    'd-flex flex-column w-100 gap-2': !isLgUp
                  })}
                >
                  <div style={{ width: isLgUp ? '160px' : '100%' }}>
                    <Select<CountryOption>
                      menuShouldScrollIntoView={true}
                      closeMenuOnSelect={true}
                      isSearchable={true}
                      onChange={(e: SingleValue<CountryOption>) => {
                        if (!e) return;
                        setSelectedPlace(e);
                        const location = geoLocationsData.data.find(
                          item => item.code === e.value
                        );
                        const { official_locaguage_code } = location ?? {
                          official_locaguage_code: null
                        };

                        let defaulrLanguage = {
                          code: 'en',
                          fa_name: 'انگلیسی'
                        };
                        if (official_locaguage_code) {
                          const language = geoLanguagesData.data.find(
                            item => item.code === official_locaguage_code
                          );
                          if (language) defaulrLanguage = language;
                        }

                        setSelectedLanguage({
                          value: defaulrLanguage.code,
                          label: defaulrLanguage.fa_name
                        });
                      }}
                      options={geoLocationsCountries}
                      classNamePrefix="react-select"
                      value={selectedPlace}
                      maxMenuHeight={300}
                      styles={{
                        control: baseStyles => ({
                          ...baseStyles,
                          paddingLeft: '0px',
                          paddingRight: '10px',
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important',
                          cursor: 'pointer',
                          borderRadius: isLgUp ? '0px 5px 5px 0px' : '5px'
                        }),
                        input: baseStyles => ({
                          ...baseStyles,
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important'
                        }),
                        menu: base => ({
                          ...base,
                          width: '200px'
                        })
                      }}
                      components={{
                        Option: OptionLayoutPlace,
                        SingleValue: SingleValueFlag
                      }}
                    />
                  </div>
                  <div style={{ width: isLgUp ? '120px' : '100%' }}>
                    <Select<SelectOption>
                      menuShouldScrollIntoView={true}
                      closeMenuOnSelect={true}
                      isSearchable={true}
                      onChange={(e: SingleValue<SelectOption>) => {
                        if (e) setSelectedLanguage(e);
                      }}
                      options={geoLanguages}
                      classNamePrefix="react-select"
                      value={selectedLanguage}
                      maxMenuHeight={300}
                      styles={{
                        control: baseStyles => ({
                          ...baseStyles,
                          paddingLeft: '0px',
                          paddingRight: '10px',
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important',
                          cursor: 'pointer',
                          borderRadius: isLgUp ? '0px' : '5px'
                        }),
                        input: baseStyles => ({
                          ...baseStyles,
                          color: 'var(--falcon-emphasise) !important',
                          boxShadow: 'none !important'
                        }),
                        menu: base => ({
                          ...base,
                          width: '150px'
                        })
                      }}
                      components={{
                        Option: OptionLayoutPlace,
                        SingleValue: SingleValueFlag
                      }}
                    />
                  </div>
                  <Form.Control
                    type="text"
                    isInvalid={!!(touched.keyword && errors.keyword)}
                    isValid={!!(touched.keyword && !errors.keyword)}
                    placeholder="کلمه مدنظر را جستجو کنید"
                    value={values.keyword}
                    onChange={handleChange}
                    disabled={isSubmitted}
                    onBlur={handleBlur}
                    name="keyword"
                  />
                  <ChildrensTooltip
                    placement="top"
                    className="p-0"
                    html={isAccessAllowed() ? '' : 'شما دسترسی لازم را ندارید'}
                  >
                    <Button
                      variant="falcon-primary"
                      className={
                        isLgUp
                          ? 'd-flex align-items-center h-100 rounded-start-0'
                          : 'w-100'
                      }
                      type="submit"
                      disabled={
                        !isAccessAllowed() ||
                        isSubmitted ||
                        (accessData?.data?.budget_used ?? 0) >=
                          (accessData?.data?.total_budget ?? 0)
                      }
                    >
                      <span className="me-1 fs--1">
                        {isSubmitted ? (
                          <>
                            <Spinner
                              style={{
                                width: '16px',
                                height: '16px'
                              }}
                              className="me-1"
                            />
                            درحال اجرا
                          </>
                        ) : (
                          <>
                            <span className="fs-0 me-1">اجرا</span>
                            <span className="opacity-75 fs--1">
                              (
                              {accessData?.data?.budget_used
                                ?.toString()
                                ?.toPersianDigit()}{' '}
                              از
                              {accessData?.data?.total_budget
                                ?.toString()
                                ?.toPersianDigit()}
                              <HelpTooltip
                                color={getColor('black')}
                                placement="top"
                                html="این محدودیت ماهانه است."
                              />
                              )
                            </span>
                          </>
                        )}
                      </span>
                    </Button>
                  </ChildrensTooltip>
                </div>

                <div>
                  {errors.keyword ? (
                    <Form.Label className="text-danger">
                      {errors.keyword}
                    </Form.Label>
                  ) : null}
                </div>
              </Form>
            </div>
          )}
        </Formik>
      ) : (
        <TextLoading width={50} height={40} />
      )}
    </>
  );
};

export default NewSerpForm;
